import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";

const LINKS = [
  { to: "/", label: "Home", testId: "nav-link-home" },
  { to: "/about", label: "About Us", testId: "nav-link-about" },
  { to: "/what-we-do", label: "What We Do", testId: "nav-link-what-we-do" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const linkClass = ({ isActive }) =>
    `relative text-[15px] font-medium tracking-[0.01em] transition-colors duration-300 hover:text-nh-green after:absolute after:-bottom-1.5 after:left-0 after:h-[2px] after:rounded-full after:bg-nh-gold after:transition-all after:duration-300 ${
      isActive ? "text-nh-green after:w-full" : "text-nh-body after:w-0 hover:after:w-full"
    }`;

  return (
    <header
      data-testid="site-navbar"
      className={`sticky top-0 z-50 border-b bg-white/90 backdrop-blur-xl transition-all duration-300 ${
        scrolled ? "border-nh-deep/10 shadow-soft" : "border-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3.5 md:px-12 md:py-4">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3">
          <img
            src="/logo.png"
            alt="Nurturing Happiness logo"
            className="h-11 w-11 object-contain md:h-14 md:w-14"
          />
          <span className="sr-only">Nurturing Happiness</span>
        </Link>

        <nav className="hidden items-center gap-10 md:flex" aria-label="Main navigation">
          {LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.to === "/"} className={linkClass} data-testid={l.testId}>
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/contact"
            data-testid="nav-cta-contact"
            className="rounded-full bg-nh-green px-6 py-2.5 text-[15px] font-semibold text-white shadow-soft transition-all duration-300 hover:-translate-y-0.5 hover:bg-nh-deep hover:shadow-lift active:scale-95"
          >
            Contact Us
          </Link>
        </nav>

        <button
          type="button"
          data-testid="nav-hamburger"
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          className="flex h-11 w-11 items-center justify-center rounded-full border border-nh-deep/15 text-nh-deep transition-colors hover:bg-nh-mint md:hidden"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            aria-label="Mobile navigation"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.28, ease: "easeOut" }}
            className="overflow-hidden border-t border-nh-deep/10 bg-white md:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-5">
              {LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  data-testid={`mobile-${l.testId}`}
                  className={({ isActive }) =>
                    `rounded-xl px-4 py-3 text-base font-medium transition-colors ${
                      isActive ? "bg-nh-mint text-nh-green" : "text-nh-body hover:bg-nh-mint"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <Link
                to="/contact"
                data-testid="mobile-nav-cta-contact"
                className="mt-2 rounded-full bg-nh-green px-4 py-3 text-center text-base font-semibold text-white"
              >
                Contact Us
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
